import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { message } from 'antd'
import {
  createBrandOnboardingSession,
  downloadBrandOnboardingDocument,
  finalizeBrandOnboardingSession,
  getBrandOnboardingAssetUrl,
  getBrandOnboardingProfile,
  getBrandOnboardingResolvedUrl,
  getBrandOnboardingSessionStatus,
  streamBrandGuidelinesUpload,
  streamBrandOnboardingChat
} from '../../../../../services/api/aiVideoGeneration'

const MAX_GUIDELINE_FILE_SIZE = 50 * 1024 * 1024
const ACCEPTED_GUIDELINE_EXTENSIONS = [
  'pdf',
  'docx',
  'doc',
  'pptx',
  'png',
  'jpg',
  'jpeg',
  'svg',
  'txt',
  'md'
]

const getResponseData = response => response?.data?.data || response?.data || {}

const createMessage = (role, content, extra = {}) => ({
  id: `${role}-${Date.now()}-${Math.random()}`,
  role,
  content,
  ...extra
})

const getFileExtension = fileName => {
  const parts = String(fileName || '').split('.')
  return parts.length > 1 ? parts.pop().toLowerCase() : ''
}

const isGuidelineFileSupported = file => {
  const extension = getFileExtension(file?.name)
  return ACCEPTED_GUIDELINE_EXTENSIONS.includes(extension)
}

const buildMetadataState = metadata => ({
  phase: metadata?.phase || 'discovery',
  completionPercentage: Number(metadata?.completion_percentage || 0),
  topicsCovered: metadata?.topics_covered || [],
  topicsRemaining: metadata?.topics_remaining || []
})

const getBrandSessionIdFromRoute = () => {
  const params = new URLSearchParams(window.location.search)
  return params.get('brandSessionId')
}

const persistBrandSession = activeSessionId => {
  if (!activeSessionId) return

  const params = new URLSearchParams(window.location.search)
  if (params.get('brandSessionId') === String(activeSessionId)) return

  params.set('brandSessionId', activeSessionId)
  const queryString = params.toString()
  const nextUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}`
  window.history.replaceState(null, '', nextUrl)
}

const normalizeChatHistory = chatHistory => {
  if (!Array.isArray(chatHistory)) return []

  return chatHistory
    .filter(item => item && item.content)
    .map((item, index) => {
      const role = item.role || 'assistant'
      const rawContent = String(item.content || '')

      return createMessage(
        role,
        role === 'assistant'
          ? cleanBrandAssistantContent(rawContent)
          : rawContent,
        {
          id: `${role}-${item.timestamp || index}`,
          rawContent,
          streaming: false,
          timestamp: item.timestamp
        }
      )
    })
}

const cleanBrandAssistantContent = content => {
  const text = String(content || '')

  if (!text) return ''

  const fencedJsonIndex = text.search(/```json/i)
  if (fencedJsonIndex !== -1) {
    return text.slice(0, fencedJsonIndex).trim()
  }

  const metadataObjectIndex = text.search(
    /\{\s*"phase"\s*:|"topics_covered"\s*:|"topics_remaining"\s*:|"completion_percentage"\s*:/
  )

  if (metadataObjectIndex !== -1) {
    return text.slice(0, metadataObjectIndex).trim()
  }

  return text
}

const useBrandOnboarding = ({
  organizationId,
  onComplete,
  initialSessionId,
  shouldCreateSession = true
}) => {
  const [sessionId, setSessionId] = useState(null)
  const [sessionStatus, setSessionStatus] = useState('idle')
  const [viewMode, setViewMode] = useState('choice')
  const [messages, setMessages] = useState([])
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [metadata, setMetadata] = useState(buildMetadataState({}))
  const [brandProfile, setBrandProfile] = useState(null)
  const [brandDocumentUrl, setBrandDocumentUrl] = useState('')
  const [brandImageUrl, setBrandImageUrl] = useState('')
  const [isDownloadingBrandDocument, setIsDownloadingBrandDocument] = useState(false)
  const [isCreatingSession, setIsCreatingSession] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [isFinalizing, setIsFinalizing] = useState(false)
  const [error, setError] = useState('')

  const hasCreatedSessionRef = useRef(false)
  const assetUrlsRef = useRef([])

  const canFinalize = useMemo(() => {
    return (
      metadata.phase === 'confirmation' ||
      metadata.completionPercentage >= 80 ||
      sessionStatus === 'completed'
    )
  }, [metadata, sessionStatus])

  const appendUserMessage = useCallback(content => {
    setMessages(prev => [...prev, createMessage('user', content)])
  }, [])

  const appendAssistantToken = useCallback(token => {
    setMessages(prev => {
      const nextMessages = [...prev]
      const lastMessage = nextMessages[nextMessages.length - 1]

      if (lastMessage && lastMessage.role === 'assistant' && lastMessage.streaming) {
        const rawContent = `${lastMessage.rawContent || lastMessage.content || ''}${token}`
        nextMessages[nextMessages.length - 1] = {
          ...lastMessage,
          rawContent,
          content: cleanBrandAssistantContent(rawContent)
        }
        return nextMessages
      }

      const rawContent = String(token || '')
      return [
        ...nextMessages,
        createMessage('assistant', cleanBrandAssistantContent(rawContent), {
          rawContent,
          streaming: true
        })
      ]
    })
  }, [])

  const markAssistantDone = useCallback(() => {
    setMessages(prev => {
      const nextMessages = prev.map((item, index) =>
        index === prev.length - 1 && item.role === 'assistant'
          ? {
              ...item,
              content: cleanBrandAssistantContent(
                item.rawContent || item.content
              ),
              streaming: false
            }
          : item
      )
      const lastMessage = nextMessages[nextMessages.length - 1]

      if (
        lastMessage &&
        lastMessage.role === 'assistant' &&
        !String(lastMessage.content || '').trim()
      ) {
        return nextMessages.slice(0, -1)
      }

      return nextMessages
    })
  }, [])

  const handleMetadata = useCallback(nextMetadata => {
    setMetadata(buildMetadataState(nextMetadata))
  }, [])

  const createSession = useCallback(async () => {
    if (hasCreatedSessionRef.current) return

    hasCreatedSessionRef.current = true
    setIsCreatingSession(true)
    setError('')

    try {
      const response = await createBrandOnboardingSession(organizationId)
      const data = getResponseData(response)
      const initialMessage = data.initial_message

      setSessionId(data.session_id)
      setSessionStatus(data.status || 'in_progress')
      persistBrandSession(data.session_id)

      if (initialMessage?.content) {
        setMessages([
          createMessage(initialMessage.role || 'assistant', initialMessage.content)
        ])
      }
    } catch (err) {
      const errorMessage =
        err?.message || 'Unable to start brand onboarding session'
      setError(errorMessage)
      hasCreatedSessionRef.current = false
      message.error(errorMessage)
    } finally {
      setIsCreatingSession(false)
    }
  }, [organizationId])

  const loadExistingSession = useCallback(
    async activeSessionId => {
      if (!activeSessionId) return false

      hasCreatedSessionRef.current = true
      setIsCreatingSession(true)
      setError('')

      try {
        const response = await getBrandOnboardingSessionStatus(activeSessionId)
        const data = getResponseData(response)
        const nextSessionId = data.session_id || activeSessionId
        const chatMessages = normalizeChatHistory(data.chat_history)
        const hasUserTurn = chatMessages.some(item => item.role === 'user')
        const uploaded = data.uploaded_files || []

        setSessionId(nextSessionId)
        setSessionStatus(data.status || 'in_progress')
        setMessages(chatMessages)
        setUploadedFiles(uploaded)
        setMetadata(
          buildMetadataState({
            phase: data.phase,
            completion_percentage: data.completion_percentage,
            topics_covered: data.topics_covered,
            topics_remaining: data.topics_remaining
          })
        )
        persistBrandSession(nextSessionId)

        if (data.status === 'completed') {
          await loadCompletedProfile(data, nextSessionId)
        } else {
          setViewMode(hasUserTurn || uploaded.length ? 'chat' : 'choice')
        }

        return true
      } catch (err) {
        console.warn('Failed to hydrate existing brand onboarding session:', err)
        setSessionId(activeSessionId)
        setSessionStatus('in_progress')
        setMetadata(buildMetadataState({}))
        persistBrandSession(activeSessionId)
        setViewMode('choice')
        return true
      } finally {
        setIsCreatingSession(false)
      }
    },
    []
  )

  useEffect(() => {
    const routeSessionId = getBrandSessionIdFromRoute()
    const existingSessionId = routeSessionId || initialSessionId

    if (existingSessionId) {
      loadExistingSession(existingSessionId)
      return
    }

    if (!shouldCreateSession) return

    createSession()
  }, [createSession, initialSessionId, loadExistingSession, shouldCreateSession])

  useEffect(() => {
    return () => {
      assetUrlsRef.current.forEach(url => URL.revokeObjectURL(url))
    }
  }, [])

  const runStream = useCallback(
    async streamRunner => {
      setIsStreaming(true)
      setError('')

      try {
        await streamRunner({
          onToken: appendAssistantToken,
          onMetadata: handleMetadata,
          onDone: markAssistantDone,
          onFileInfo: fileInfo => {
            setUploadedFiles(prev => [...prev, fileInfo])
          }
        })
      } catch (err) {
        markAssistantDone()
        const errorMessage = err?.message || 'Brand onboarding stream failed'
        setError(errorMessage)
        message.error(errorMessage)
      } finally {
        setIsStreaming(false)
      }
    },
    [appendAssistantToken, handleMetadata, markAssistantDone]
  )

  const startFromScratch = useCallback(async () => {
    if (!sessionId || isStreaming) return

    persistBrandSession(sessionId)
    setViewMode('chat')

    const hasExistingConversation = messages.some(
      item =>
        item.role === 'user' ||
        (item.role === 'assistant' &&
          String(item.content || '').trim() &&
          messages.length > 1)
    )

    if (hasExistingConversation) {
      return
    }

    appendUserMessage('Start from scratch')
    await runStream(handlers =>
      streamBrandOnboardingChat(
        sessionId,
        "I don't have brand guidelines. Let's start from scratch.",
        handlers
      )
    )
  }, [
    appendUserMessage,
    isStreaming,
    messages,
    runStream,
    sessionId
  ])

  const uploadGuidelines = useCallback(
    async file => {
      if (!sessionId || isStreaming || !file) return false

      if (!isGuidelineFileSupported(file)) {
        message.error(
          'Unsupported file type. Upload PDF, DOCX, PPTX, image, TXT, or MD files.'
        )
        return false
      }

      if (file.size > MAX_GUIDELINE_FILE_SIZE) {
        message.error('File size must be 50 MB or less.')
        return false
      }

      setViewMode('chat')
      persistBrandSession(sessionId)
      appendUserMessage(`Uploaded ${file.name}`)

      await runStream(handlers =>
        streamBrandGuidelinesUpload(sessionId, file, handlers)
      )

      return false
    },
    [appendUserMessage, isStreaming, runStream, sessionId]
  )

  const sendMessage = useCallback(
    async text => {
      const cleanText = String(text || '').trim()
      if (!sessionId || !cleanText || isStreaming) return

      appendUserMessage(cleanText)
      await runStream(handlers =>
        streamBrandOnboardingChat(sessionId, cleanText, handlers)
      )
    },
    [appendUserMessage, isStreaming, runStream, sessionId]
  )

  const returnToSetupOptions = useCallback(() => {
    if (isStreaming || isFinalizing) return
    setViewMode('choice')
  }, [isFinalizing, isStreaming])

  const loadBrandAssets = useCallback(async (activeSessionId, options = {}) => {
    const { loadImage = true, loadDocument = true } = options

    if (loadImage) {
      try {
        const imageUrl = await getBrandOnboardingAssetUrl(
          activeSessionId,
          'image'
        )

        if (imageUrl) {
          if (imageUrl.startsWith('blob:')) {
            assetUrlsRef.current = [...assetUrlsRef.current, imageUrl]
          }
          setBrandImageUrl(imageUrl)
        }
      } catch (err) {
        console.warn('Failed to load brand onboarding image:', err)
        setBrandImageUrl('')
      }
    }

    if (loadDocument) {
      try {
        const documentUrl = await getBrandOnboardingAssetUrl(
          activeSessionId,
          'document'
        )

        if (documentUrl) {
          if (documentUrl.startsWith('blob:')) {
            assetUrlsRef.current = [...assetUrlsRef.current, documentUrl]
          }
          setBrandDocumentUrl(documentUrl)
        }
      } catch (err) {
        console.warn('Failed to load brand onboarding document:', err)
      }
    }
  }, [])

  const loadCompletedProfile = useCallback(
    async (completedData, activeSessionId = sessionId) => {
      let responseData = completedData || {}
      let profile = completedData?.brand_profile

      if (!profile && activeSessionId) {
        try {
          const profileResponse = await getBrandOnboardingProfile(
            activeSessionId
          )
          responseData = getResponseData(profileResponse)
          profile = responseData.brand_profile || responseData
        } catch (err) {
          console.warn('Failed to load brand profile:', err)
        }
      }

      if (profile) {
        setBrandProfile(profile)
      }

      const directDocumentUrl = getBrandOnboardingResolvedUrl(
        responseData.brand_document_url
      )
      const fallbackDocumentUrl = activeSessionId
        ? getBrandOnboardingResolvedUrl(`/sessions/${activeSessionId}/document`)
        : ''
      const documentUrl = directDocumentUrl || fallbackDocumentUrl

      if (documentUrl) {
        setBrandDocumentUrl(documentUrl)
      }

      if (activeSessionId) {
        await loadBrandAssets(activeSessionId, {
          loadDocument: !documentUrl
        })
      }

      setSessionStatus('completed')
      setViewMode('summary')
    },
    [loadBrandAssets, sessionId]
  )

  const pollFinalization = useCallback(async () => {
    if (!sessionId) return

    for (let attempt = 0; attempt < 20; attempt += 1) {
      const response = await getBrandOnboardingSessionStatus(sessionId)
      const data = getResponseData(response)

      setSessionStatus(data.status || 'finalizing')
      if (typeof data.completion_percentage === 'number') {
        setMetadata(prev => ({
          ...prev,
          completionPercentage: data.completion_percentage
        }))
      }

      if (data.status === 'completed') {
        await loadCompletedProfile(data)
        return
      }

      if (data.status === 'error') {
        throw new Error('Brand profile finalization failed')
      }

      await new Promise(resolve => setTimeout(resolve, 3000))
    }

    throw new Error('Brand profile is still finalizing. Please try again.')
  }, [loadCompletedProfile, sessionId])

  const finalizeProfile = useCallback(async () => {
    if (!sessionId || isFinalizing) return false

    setIsFinalizing(true)
    setError('')

    try {
      const response = await finalizeBrandOnboardingSession(sessionId)
      const data = getResponseData(response)

      setSessionStatus(data.status || 'finalizing')

      if (data.status === 'completed') {
        await loadCompletedProfile(data)
      } else {
        await pollFinalization()
      }

      return true
    } catch (err) {
      const errorMessage = err?.message || 'Failed to finalize brand profile'
      setError(errorMessage)
      message.error(errorMessage)
      return false
    } finally {
      setIsFinalizing(false)
    }
  }, [
    isFinalizing,
    loadCompletedProfile,
    pollFinalization,
    sessionId
  ])

  const continueToVideo = useCallback(() => {
    onComplete && onComplete({ sessionId, brandProfile })
  }, [brandProfile, onComplete, sessionId])

  const editBrandProfile = useCallback(() => {
    if (isStreaming || isFinalizing) return

    if (sessionId) {
      persistBrandSession(sessionId)
    }

    setViewMode('chat')
  }, [isFinalizing, isStreaming, sessionId])

  const viewBrandProfile = useCallback(() => {
    if (isStreaming || isFinalizing || !brandProfile) return
    setViewMode('summary')
  }, [brandProfile, isFinalizing, isStreaming])

  const downloadBrandDocument = useCallback(async () => {
    if (!sessionId || isDownloadingBrandDocument) return

    setIsDownloadingBrandDocument(true)

    try {
      await downloadBrandOnboardingDocument(
        sessionId,
        brandProfile?.organization_name || 'brand-profile'
      )
    } catch (err) {
      const errorMessage = err?.message || 'Failed to download brand document'
      message.error(errorMessage)
    } finally {
      setIsDownloadingBrandDocument(false)
    }
  }, [brandProfile, isDownloadingBrandDocument, sessionId])

  return {
    sessionId,
    sessionStatus,
    viewMode,
    messages,
    uploadedFiles,
    metadata,
    brandProfile,
    brandDocumentUrl,
    brandImageUrl,
    isCreatingSession,
    isStreaming,
    isFinalizing,
    isDownloadingBrandDocument,
    error,
    canFinalize,
    acceptedExtensions: ACCEPTED_GUIDELINE_EXTENSIONS,
    startFromScratch,
    uploadGuidelines,
    sendMessage,
    returnToSetupOptions,
    editBrandProfile,
    viewBrandProfile,
    downloadBrandDocument,
    finalizeProfile,
    continueToVideo,
    retryCreateSession: createSession
  }
}

export default useBrandOnboarding
